/**
 * BlocksPreviewSlot
 *
 * Unified behaviour slot for both `insight_preview` and `report_preview`
 * experiences. Renders blocks with fullscreen expansion and optional
 * footer CTAs (Slack integration, PDF download).
 *
 * The Schedule CTA is temporarily disabled client-side (backend support
 * retained) — uncomment the blocks tagged [schedule-cta] to restore it.
 *
 * **Block source (dual-mode):**
 * - Inline: `action.data.blocks` provided directly by the backend (preferred)
 * - Fetched: Falls back to `useInsightReports(action.data.insight_id)` when
 *   no inline blocks are present (backward compat for older report_preview cards)
 *
 * **CTAs:**
 * - Shown when `action.experience === 'insight_preview'`
 * - Hidden for `report_preview` experience
 *
 * Replaces the former `InsightPreviewSlot` and `ReportPreviewSlot` components.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence } from 'motion/react';
import { Maximize2, ChevronDown } from 'lucide-react';
import {
  toast,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@strivelabs/ui-core';
import { Button, IconButton } from '@strivelabs/ui-core/v2';
import { AgentThoughtsCard, LoadingSkeleton } from '@strivelabs/ui-blocks';
import {
  SlotCanvasLayout,
  SlotCanvasPreview,
  useRegisterSlotCanvas,
  useSlotCanvasContext,
} from '@/components/ai-elements/slot-canvas';
import { useSlotCanvasStore } from '@/store/slotCanvasStore';
import { EXPERIENCE_LABELS } from '@/components/ai-elements/slot-navigator/slot-navigator-utils';
import { log } from '@/lib/logger';
import type {
  BehaviourAgentExperienceAction,
  UIActionEvent,
  UIBlock,
  ContainerBlock,
  InsightCardBlock,
  PlanCardBlock,
  ReportBlock,
} from '@/types/ui-blocks';
import { isFlatReportData, isMultiPageReportData } from '@/types/ui-blocks';
import { dispatchBlock, isContainer, renderContainerBlock } from '../block-renderer-utils';
import { InsightCardBlockRenderer } from '../insight-card-block-renderer';
import { useChatMetadata } from '@/contexts/ChatMetadataContext';
import { useUpdateBehaviourActionState } from '@/hooks/useUpdateBehaviourActionState';
// [schedule-cta] import { ScheduleReportOverlay } from '@/components/schedule/ScheduleReportOverlay';
import { SendToSlackOverlay } from '@/components/slack/SendToSlackOverlay';
import { useGeneratePdfFromBlocks } from '@/hooks/useGeneratePdfFromBlocks';
import { useInsightReports } from '@/hooks/useInsights';
import { REPORT_TYPE } from '@/constants/reports';
import {
  FullscreenSlotOverlay,
  SlotContainer,
  downloadPdfFromUrl,
  toPdfFilename,
  isInsightCardBlock,
  isPlanCardBlock,
  mapPlanCardStepsToAgentSteps,
} from './shared';

// Slot action icons
// [schedule-cta] import scheduleIcon from '@/assets/slot/schedule.svg';
import slackLogo from '@/assets/slot/slack-logo.png';
import downloadIcon from '@/assets/slot/download.svg';

// ---------------------------------------------------------------------------
// Block rendering
// ---------------------------------------------------------------------------

/**
 * Recursively render blocks within the slot.
 * Handles InsightCardBlock, PlanCardBlock, ContainerBlock, and regular UIBlock.
 */
const renderSlotBlocks = (
  blocks: (UIBlock | ContainerBlock | InsightCardBlock | PlanCardBlock)[],
  onUIAction: (event: UIActionEvent) => void,
  readOnly?: boolean
): React.ReactNode[] => {
  return blocks.map((block, index) => {
    if (isInsightCardBlock(block)) {
      return (
        <div key={`insight-card-${index}`}>
          <InsightCardBlockRenderer block={block} onUIAction={onUIAction} />
        </div>
      );
    }

    if (isPlanCardBlock(block as ReportBlock)) {
      const planBlock = block as PlanCardBlock;
      // Wrap in a div so the parent slot's `space-y-7` applies
      // a consistent gap between this block and its neighbours,
      // matching the InsightCardBlock / dispatchBlock branches.
      return (
        <div key={planBlock.id ?? `plan-card-${index}`}>
          <AgentThoughtsCard
            steps={mapPlanCardStepsToAgentSteps(planBlock.content.steps)}
            label={planBlock.content.label}
            detailButtonLabel={planBlock.content.detail_button_label}
            dialogTitle={planBlock.content.dialog_title}
            dialogDescription={planBlock.content.dialog_description}
          />
        </div>
      );
    }

    const uiBlock = block as UIBlock | ContainerBlock;

    if (isContainer(uiBlock)) {
      return renderContainerBlock(
        uiBlock as ContainerBlock,
        (innerBlocks) => renderSlotBlocks(innerBlocks, onUIAction, readOnly),
        { includeWrapper: true, index }
      );
    }

    return (
      <div key={`slot-block-${index}`}>
        {dispatchBlock({ block: uiBlock, onUIAction, readOnly })}
      </div>
    );
  });
};

// ---------------------------------------------------------------------------
// Fetch-path helpers (backward compat for old report_preview cards)
// ---------------------------------------------------------------------------

/**
 * Resolves the target page's blocks from a MULTI_PAGE report payload.
 */
function resolveMultiPageBlocks(
  pageGroups: Array<{ id: string; pages: Array<{ id?: string; blocks: ReportBlock[] }> }>,
  pageGroupId?: string,
  pageId?: string
): ReportBlock[] {
  const group =
    (pageGroupId ? pageGroups.find((g) => g.id === pageGroupId) : undefined) ?? pageGroups[0];

  if (!group) return [];

  const page =
    (pageId ? group.pages.find((p) => p.id === pageId) : undefined) ?? group.pages[0];

  return page?.blocks ?? [];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface BlocksPreviewSlotProps {
  /** Stable slot id (behaviour.id) used for canvas registration + SlotNavigator */
  slotId?: string;
  action: BehaviourAgentExperienceAction;
  onUIAction: (event: UIActionEvent) => void;
  readOnly?: boolean;
  chatHistoryId?: string;
  behaviourActionState?: Record<string, unknown>;
  /**
   * Suppress the inline preview chip in canvas mode. Used when the slot is
   * driven by a host panel (e.g. the briefing) that renders its own artifact
   * card and opens the slot on demand, so there is no in-stream chip to show.
   */
  hidePreviewChip?: boolean;
  /** Register without auto-opening — the host controls when it opens. */
  autoOpen?: boolean;
}

export function BlocksPreviewSlot({
  slotId,
  action,
  onUIAction,
  readOnly,
  chatHistoryId,
  behaviourActionState,
  hidePreviewChip,
  autoOpen,
}: BlocksPreviewSlotProps) {
  const canvasCtx = useSlotCanvasContext();
  const isCanvasMode = canvasCtx.mode === 'canvas';
  const canvasBodyMount = useSlotCanvasStore((s) => s.bodyMountElement);
  const resolvedSlotId = slotId ?? action.id;
  const resolvedTitle = action.data?.title ?? EXPERIENCE_LABELS[action.experience];
  const canvasRegistration = useRegisterSlotCanvas({
    id: resolvedSlotId,
    experience: action.experience,
    title: resolvedTitle,
    chatHistoryId,
    autoOpen,
    // When the chip is hidden (briefing artifact), the canvas has no in-stream
    // card to reopen from, so the frame offers collapse-only instead of close.
    hasInStreamCard: !hidePreviewChip,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  // [schedule-cta] const [isScheduleDrawerOpen, setIsScheduleDrawerOpen] = useState(false);
  const [isSendToSlackDrawerOpen, setIsSendToSlackDrawerOpen] = useState(false);
  // [schedule-cta] const [shouldAnimateTowardsIcon, setShouldAnimateTowardsIcon] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const [chatRoot, setChatRoot] = useState<HTMLElement | null>(null);

  // -- Block source resolution ------------------------------------------------

  // Inline blocks (insight_preview cards + new report_preview cards)
  const directBlocks = action.data?.blocks;
  const hasDirectBlocks = Array.isArray(directBlocks) && directBlocks.length > 0;

  // Fetch path (old report_preview cards with insight_id but no inline blocks)
  const insightId = action.data?.insight_id ?? '';
  const shouldFetch = !!insightId && !hasDirectBlocks;
  const { data: reports, isLoading, isError } = useInsightReports(insightId, {
    enabled: shouldFetch,
  });

  // Resolve blocks from fetched reports (only used when shouldFetch is true)
  const fetchedBlocks: ReportBlock[] = (() => {
    if (!reports || reports.length === 0) return [];

    const targetReportId = action.data?.insight_report_id;
    const targetPageGroupId = action.data?.page_group_id;
    const targetPageId = action.data?.page_id;

    // 1. Direct report lookup by insight_report_id
    const targetReport = targetReportId
      ? reports.find((r) => String(r.id) === String(targetReportId))
      : undefined;

    // 2. If no direct match, search MULTI_PAGE reports by page_group_id
    const report =
      targetReport ??
      (targetPageGroupId &&
        reports.find(
          (r) =>
            (r.type || REPORT_TYPE.FLAT) === REPORT_TYPE.MULTI_PAGE &&
            isMultiPageReportData(r.data) &&
            r.data.page_groups.some((g) => g.id === targetPageGroupId)
        )) ??
      // 3. Fallback to first report
      reports[0];

    const reportType = report.type || REPORT_TYPE.FLAT;

    if (reportType === REPORT_TYPE.FLAT) {
      if (isFlatReportData(report.data)) {
        return report.data.ui.blocks;
      }
      return [];
    }

    if (isMultiPageReportData(report.data)) {
      return resolveMultiPageBlocks(report.data.page_groups, targetPageGroupId, targetPageId);
    }
    return [];
  })();

  // Final blocks to render — inline takes precedence
  const resolvedBlocks = hasDirectBlocks
    ? (directBlocks as (UIBlock | ContainerBlock | InsightCardBlock | PlanCardBlock)[])
    : (fetchedBlocks as (UIBlock | ContainerBlock | InsightCardBlock | PlanCardBlock)[]);

  // PDF / Schedule / Slack helpers don't render plan_card blocks; strip them
  // so the narrower input types of those helpers stay satisfied. This branch
  // is only reachable behind `showCTAs` (insight_preview), so plan_card never
  // shows up here at runtime — the filter is just a TS narrowing.
  const blocksForExport: (UIBlock | ContainerBlock | InsightCardBlock)[] =
    resolvedBlocks.filter(
      (b): b is UIBlock | ContainerBlock | InsightCardBlock =>
        !isPlanCardBlock(b as ReportBlock)
    );

  // -- CTA visibility ---------------------------------------------------------

  const showCTAs = action.experience === 'insight_preview';

  // -- CTA hooks & state (always called, only used when showCTAs is true) -----

  const { generatePdf, isGenerating: isGeneratingPdf } = useGeneratePdfFromBlocks();
  const { workflowInstanceId, workflowExecutionId } = useChatMetadata();

  const actionState =
    action.id && behaviourActionState
      ? (behaviourActionState[action.id] as Record<string, unknown> | undefined)
      : undefined;
  // [schedule-cta] const initialScheduled = (actionState?.scheduled as boolean) ?? false;
  const initialSentToSlack = (actionState?.sent_to_slack as boolean) ?? false;
  // [schedule-cta] const [localScheduledState, setLocalScheduledState] = useState(initialScheduled);
  const [localSentToSlackState, setLocalSentToSlackState] = useState(initialSentToSlack);

  /* [schedule-cta]
  const scheduleMutation = useUpdateBehaviourActionState({
    onSuccess: () => {
      setLocalScheduledState(true);
      toast({ title: 'Report scheduled successfully', variant: 'default' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to schedule report',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
    },
  });
  */

  const sendToSlackMutation = useUpdateBehaviourActionState({
    onSuccess: () => {
      setLocalSentToSlackState(true);
      toast({ title: 'Sent to Slack successfully', variant: 'default' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to send to Slack',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
    },
  });

  // -- CTA handlers -----------------------------------------------------------

  /* [schedule-cta]
  const handleScheduleReport = async () => {
    if (!workflowInstanceId || !workflowExecutionId || !action.id) {
      toast({ title: 'Cannot schedule report', description: 'Missing required IDs', variant: 'destructive' });
      return;
    }
    if (!chatHistoryId) {
      toast({
        title: 'Cannot schedule report',
        description: 'Message ID not available yet. Please wait for the message to complete.',
        variant: 'destructive',
      });
      return;
    }
    setIsScheduleDrawerOpen(true);
  };

  const handleScheduleSuccess = () => {
    if (workflowInstanceId && workflowExecutionId && chatHistoryId && action.id) {
      setShouldAnimateTowardsIcon(true);
      scheduleMutation.mutate({
        workflowInstanceId,
        workflowExecutionId,
        chatHistoryId,
        behaviourActionStateId: action.id,
        state: { scheduled: true },
      });
      setTimeout(() => {
        setIsScheduleDrawerOpen(false);
      }, 100);
    } else {
      log('error', 'Missing required IDs to update behaviour action state after scheduling.');
      setIsScheduleDrawerOpen(false);
    }
  };

  const handleOverlayExitComplete = () => {
    if (shouldAnimateTowardsIcon) {
      setShouldAnimateTowardsIcon(false);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('report-scheduled'));
      }, 100);
    }
  };
  */

  const handleSendToSlack = () => {
    if (!workflowExecutionId) {
      toast({ title: 'Cannot send to Slack', description: 'Missing workflow execution ID', variant: 'destructive' });
      return;
    }
    setIsSendToSlackDrawerOpen(true);
  };

  const handleSendToSlackSuccess = () => {
    if (workflowInstanceId && workflowExecutionId && chatHistoryId && action.id) {
      sendToSlackMutation.mutate({
        workflowInstanceId,
        workflowExecutionId,
        chatHistoryId,
        behaviourActionStateId: action.id,
        state: { sent_to_slack: true },
      });
      setIsSendToSlackDrawerOpen(false);
    } else {
      log('error', 'Missing required IDs to update behaviour action state after sending to Slack.');
      setIsSendToSlackDrawerOpen(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!resolvedBlocks || resolvedBlocks.length === 0) {
      toast({ title: 'Cannot download report', description: 'No report content available', variant: 'destructive' });
      return;
    }
    try {
      const reportTitle = action.data?.title || 'Report';
      const pdfResult = await generatePdf({ blocks: blocksForExport, title: reportTitle });
      await downloadPdfFromUrl(pdfResult.url, toPdfFilename(reportTitle));
      toast({ title: 'Report downloaded successfully', variant: 'default' });
    } catch (error) {
      toast({
        title: 'Failed to download report',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    }
  };

  // [schedule-cta] const isScheduleDisabled = localScheduledState || scheduleMutation.isPending;

  // -- Effects ----------------------------------------------------------------

  useEffect(() => {
    if (slotRef.current) {
      const root = slotRef.current.closest<HTMLElement>('[data-chat-root]');
      if (root) setChatRoot(root);
    }
  }, []);

  // -- Render helpers ---------------------------------------------------------

  const renderBodyContent = () => {
    if (shouldFetch && isLoading) {
      return (
        <div className="p-10">
          <LoadingSkeleton />
        </div>
      );
    }
    if (resolvedBlocks.length === 0) {
      if (shouldFetch && (isError || !reports || reports.length === 0)) {
        return (
          <div className="p-10">
            <p className="text-sm text-muted-foreground">Unable to load report.</p>
          </div>
        );
      }
      return null;
    }
    return (
      <div className="p-10 space-y-7">
        {renderSlotBlocks(resolvedBlocks, onUIAction, readOnly)}
      </div>
    );
  };

  const renderCanvasHeaderActions = () => {
    if (!showCTAs) return null;
    return (
      <div className="inline-flex items-stretch rounded-lg border border-black/[0.08] bg-white overflow-hidden">
        {/* [schedule-cta] Schedule was the primary button; Send to Slack was a
            dropdown item. To restore, uncomment this button and move the Slack
            button below back into the dropdown.
        <button
          type="button"
          onClick={handleScheduleReport}
          disabled={isScheduleDisabled}
          className="flex items-center gap-2 px-2.5 py-1 text-sm text-foreground hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={localScheduledState ? 'Already scheduled' : 'Schedule report'}
          title={localScheduledState ? 'Already scheduled' : 'Schedule this report'}
        >
          <img src={scheduleIcon} alt="" className="size-[18px]" />
          <span className="font-normal tracking-[-0.28px]">Schedule report</span>
        </button>
        */}
        <button
          type="button"
          onClick={handleSendToSlack}
          className="flex items-center gap-2 px-2.5 py-1 text-sm text-foreground hover:bg-gray-50 transition-colors"
          aria-label={localSentToSlackState ? 'Re-send to Slack' : 'Send to Slack'}
        >
          <img src={slackLogo} alt="" className="size-[13px] object-contain" />
          <span className="font-normal tracking-[-0.28px]">
            {localSentToSlackState ? 'Re-send to Slack' : 'Send to Slack'}
          </span>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger
            type="button"
            aria-label="More actions"
            className="flex items-center justify-center px-1.5 border-l border-black/[0.08] text-muted-foreground hover:bg-gray-50 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <ChevronDown className="size-3.5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[200px]">
            <DropdownMenuItem
              disabled={isGeneratingPdf}
              onSelect={(event) => {
                event.preventDefault();
                handleDownloadReport();
              }}
            >
              <img src={downloadIcon} alt="" className="mr-2 size-3.5" />
              {isGeneratingPdf ? 'Generating PDF…' : 'Download report'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const renderScheduleAndSlackOverlays = () => (
    <>
      {/* [schedule-cta]
      {showCTAs && (
        <AnimatePresence mode="wait" onExitComplete={handleOverlayExitComplete}>
          {isScheduleDrawerOpen && (
            <ScheduleReportOverlay
              key="schedule-overlay"
              isOpen={isScheduleDrawerOpen}
              workflowExecutionId={workflowExecutionId || ''}
              blocks={blocksForExport}
              title={action.data?.title}
              onClose={() => setIsScheduleDrawerOpen(false)}
              onSuccess={handleScheduleSuccess}
              animateTowardsIcon={shouldAnimateTowardsIcon}
            />
          )}
        </AnimatePresence>
      )}
      */}
      {showCTAs && (
        <AnimatePresence mode="wait">
          {isSendToSlackDrawerOpen && (
            <SendToSlackOverlay
              key="slack-overlay"
              isOpen={isSendToSlackDrawerOpen}
              blocks={blocksForExport}
              title={action.data?.title}
              onClose={() => setIsSendToSlackDrawerOpen(false)}
              onSuccess={handleSendToSlackSuccess}
            />
          )}
        </AnimatePresence>
      )}
    </>
  );

  // Empty-state gate (shared between both modes): if we have no blocks and
  // aren't fetching, render nothing (matches legacy InsightPreviewSlot).
  const hasNothingToShow =
    !shouldFetch && resolvedBlocks.length === 0;

  // -- Canvas mode ------------------------------------------------------------
  if (isCanvasMode) {
    if (hasNothingToShow) return null;
    return (
      <>
        {!hidePreviewChip && (
          <SlotCanvasPreview
            id={resolvedSlotId}
            experience={action.experience}
            title={resolvedTitle}
            isActive={canvasRegistration.isOpen}
            onClick={canvasRegistration.open}
          />
        )}
        {canvasRegistration.isOpen &&
          canvasBodyMount &&
          createPortal(
            <SlotCanvasLayout
              title={resolvedTitle}
              subtitle={EXPERIENCE_LABELS[action.experience]}
              headerActions={renderCanvasHeaderActions()}
            >
              {renderBodyContent()}
            </SlotCanvasLayout>,
            canvasBodyMount,
          )}
        {renderScheduleAndSlackOverlays()}
      </>
    );
  }

  // -- Inline mode (legacy path, unchanged) -----------------------------------

  // Loading state (fetch path only)
  if (shouldFetch && isLoading) {
    return (
      <SlotContainer slotRef={slotRef}>
        <div className="p-10">
          <LoadingSkeleton />
        </div>
      </SlotContainer>
    );
  }

  // Error or empty state
  if (resolvedBlocks.length === 0) {
    // Fetch path: show error message for failed/empty fetches
    if (shouldFetch && (isError || !reports || reports.length === 0)) {
      return (
        <SlotContainer slotRef={slotRef}>
          <div className="p-10">
            <p className="text-sm text-muted-foreground">Unable to load report.</p>
          </div>
        </SlotContainer>
      );
    }
    // Inline path with no blocks: render nothing (matches old InsightPreviewSlot)
    return null;
  }

  return (
    <>
      <SlotContainer slotRef={slotRef}>
        {/* Expand button - positioned at top right */}
        <IconButton
          variant="secondary"
          size="2xs"
          onClick={() => setIsExpanded(true)}
          className="absolute top-2.5 right-2.5 z-10"
          aria-label="Expand to fullscreen"
        >
          <Maximize2 className="size-3.5 text-foreground" />
        </IconButton>

        {/* Slot content */}
        <div className="p-10 space-y-7">
          {renderSlotBlocks(resolvedBlocks, onUIAction, readOnly)}
        </div>

        {/* Footer with CTAs — only for insight_preview */}
        {showCTAs && (
          <div className="relative border-t border-black/[0.04] h-12 flex items-center justify-end px-2">
            {/* [schedule-cta] Left side - Schedule report. To restore, uncomment
                this button and change `justify-end` above back to `justify-between`.
            <Button
              variant="secondary"
              size="sm"
              onClick={handleScheduleReport}
              disabled={isScheduleDisabled}
              icon={<img src={scheduleIcon} alt="" className="size-[18px]" />}
              aria-label={localScheduledState ? 'Already scheduled' : 'Schedule report'}
              title={localScheduledState ? 'Already scheduled' : 'Schedule this report'}
            >
              Schedule report
            </Button>
            */}
            {/* Right side - Send to slack & Download report */}
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSendToSlack}
                icon={<img src={slackLogo} alt="" className="size-[13px] object-contain" />}
                aria-label={localSentToSlackState ? 'Re-send to Slack' : 'Send to Slack'}
              >
                {localSentToSlackState ? 'Re-send to Slack' : 'Send to Slack'}
              </Button>

              <Button
                variant="secondary"
                size="sm"
                onClick={handleDownloadReport}
                disabled={isGeneratingPdf}
                icon={<img src={downloadIcon} alt="" className="size-3.5" />}
                aria-label="Download report"
              >
                {isGeneratingPdf ? 'Generating PDF...' : 'Download report'}
              </Button>
            </div>
          </div>
        )}
      </SlotContainer>

      {/* Fullscreen overlay — portalled into the chat container */}
      <AnimatePresence>
        {isExpanded && chatRoot && (
          <FullscreenSlotOverlay onClose={() => setIsExpanded(false)} portalTarget={chatRoot}>
            {renderSlotBlocks(resolvedBlocks, onUIAction, readOnly)}
          </FullscreenSlotOverlay>
        )}
      </AnimatePresence>

      {renderScheduleAndSlackOverlays()}
    </>
  );
}
